import React, { useState } from "react";
import "./ExpenseDetails.css";
import Modal from "components/common/Modal/Modal";
import { formatDate } from "ultils/date-formatter";
import { ExpenseService } from "services/expense";
import { format } from "date-fns";

interface ExpenseDetailsProps {
  propertyDetails: any;
  expenseList: any;
  onExpenseUpdate: (updatedDetails: any) => void; // Callback for parent to handle updates
}

const ExpenseDetails: React.FC<ExpenseDetailsProps> = ({
  propertyDetails,
  expenseList,
  onExpenseUpdate,
}) => {
  const emptyExpenseDetails = {
    category: "",
    description: "",
    occurrence: "",
    amount: "",
    dateIncurred: "",
    dateAdded: "",
    parts: "",
    labor: "",
    notes: "",
    clientId: null,
    userId: null,
    propertyId: "",
    isRecurring: false,
  };

  const [title, setTitle] = useState("Add Expense");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenseDetails, setExpenseDetails] =
    React.useState(emptyExpenseDetails);

  const categories = [
    "Mortgage (P & I)",
    "Mortgage with Escrow (Insurance & Property Taxes)",
    "Utilities",
    "Maintenance",
    "Insurance",
    "Property Taxes",
    "Plumbing",
    "Electrical",
    "Landscaping",
    "Other",
  ];
  const occurrenceOptions = ["Monthly Recurring", "Annual", "One-time"];

  const openModal = () => {
    setExpenseDetails(emptyExpenseDetails);
    setTitle("Add Expense");
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const openEditModal = (selectedRecord: any) => {
    setExpenseDetails(selectedRecord);
    setTitle("Edit Expense");
    setIsModalOpen(true);
  };

  const formattedDateIncurred = expenseDetails.dateIncurred
    ? format(new Date(expenseDetails.dateIncurred), "yyyy-MM-dd")
    : "";

  const formattedDateAdded = expenseDetails.dateAdded
    ? format(new Date(expenseDetails.dateAdded), "yyyy-MM-dd")
    : "";

  const handleSubmit = async (e: React.FormEvent) => {
    title.includes("Add") ? addExpense(e) : editExpense(e);
  };

  const addExpense = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (expenseDetails.occurrence === "Monthly Recurring") {
        expenseDetails.isRecurring = true;
      }
      expenseDetails.propertyId = propertyDetails.propertyId;
      await ExpenseService.postExpense(expenseDetails);
      onExpenseUpdate(expenseDetails); // Notify the parent component of the updated details
      alert("Expense details updated successfully");
      closeModal();
    } catch (err) {
      console.error("Failed to update property:", err);
      alert("Failed to update expense details");
    }
  };

  const editExpense = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      expenseDetails.propertyId = propertyDetails.propertyId;
      console.log(expenseDetails);
      await ExpenseService.updateExpense(expenseDetails);
      onExpenseUpdate(expenseDetails); // Notify the parent component of the updated details
      alert("Expense details updated successfully");
    } catch (err) {
      console.error("Failed to update property:", err);
      alert("Failed to update expense details");
      closeModal();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === "checkbox";

    setExpenseDetails((prev: any) => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="">
      <div className={"add-property-container"}>
        <div className="header-container">
          <h1>Expenses</h1>
          <button className="btn" onClick={openModal}>
            Add Expense
          </button>
        </div>

        <div>
          <table style={{ border: 1, width: "100%", textAlign: "left" }}>
            <thead>
              <tr>
                <th>Date Incurred</th>
                <th>Category</th>
                <th>Occurrence</th>
                <th>Amount</th>
                <th>Description</th>
                <th>Date Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenseList.length > 0 ? (
                expenseList.map((expense: any) => (
                  <tr key={expense.expenseId}>
                    <td>{formatDate(expense.dateIncurred, "MM/dd/yyyy")}</td>
                    <td>{expense.category}</td>
                    <td>{expense.occurrence}</td>
                    <td>{expense.amount}</td>
                    <td>{expense.description}</td>
                    <td>
                      {formatDate(expense.dateAdded, "MM/dd/yyyy hh:mm a")}
                    </td>
                    <td>
                      <button
                        className="btn"
                        onClick={() => openEditModal(expense)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>No Expenses! Congratulations!</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Modal title={title} isOpen={isModalOpen} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="add-expense-form">
            {/* Category */}
            <div className="form-group">
              <label htmlFor="category">Category:</label>
              <select
                id="category"
                name="category"
                value={expenseDetails.category}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select a category
                </option>
                {categories.map((cat, index) => (
                  <option key={index} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="occurrence">Occurrence:</label>
              <select
                id="occurrence"
                name="occurrence"
                value={expenseDetails.occurrence || ""}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select occurrence
                </option>
                {occurrenceOptions.map((option) => (
                  <option key={option} value={option.toLowerCase()}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">Description:</label>
              <textarea
                id="description"
                name="description"
                value={expenseDetails.description}
                onChange={handleChange}
                placeholder="Enter description"
              />
            </div>

            {/* Amount */}
            <div className="form-group">
              <label htmlFor="amount">Amount:</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={expenseDetails.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                step="0.01"
                required
              />
            </div>

            {/* Date Incurred */}
            <div className="form-group">
              <label htmlFor="dateIncurred">Date Incurred:</label>
              <input
                type="date"
                id="dateIncurred"
                name="dateIncurred"
                value={formattedDateIncurred}
                onChange={handleChange}
                required
              />
            </div>

            {/* Date Added */}
            <div className="form-group">
              <label htmlFor="dateAdded">Date Added:</label>
              <input
                type="date"
                id="dateAdded"
                name="dateAdded"
                value={formattedDateAdded}
                onChange={handleChange}
              />
            </div>

            {/* Parts */}
            <div className="form-group">
              <label htmlFor="parts">Parts:</label>
              <input
                type="text"
                id="parts"
                name="parts"
                value={expenseDetails.parts}
                onChange={handleChange}
                placeholder="Enter parts"
              />
            </div>

            {/* Labor */}
            <div className="form-group">
              <label htmlFor="labor">Labor:</label>
              <input
                type="text"
                id="labor"
                name="labor"
                value={expenseDetails.labor}
                onChange={handleChange}
                placeholder="Enter labor"
              />
            </div>

            {/* Notes */}
            <div className="form-group">
              <label htmlFor="notes">Notes:</label>
              <textarea
                id="notes"
                name="notes"
                value={expenseDetails.notes}
                onChange={handleChange}
                placeholder="Enter any additional notes"
              />
            </div>

            <button type="submit" className="submit-button">
              {title.includes("Add") ? "Add Expense" : "Update Expense"}
            </button>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default ExpenseDetails;
